/**
 * Piezas de UI para las pantallas de Configuración, tomadas del patrón de
 * settings de Bankivo:
 *
 *  - SectionPageHeader: icono + título + descripción a la izquierda y la
 *    acción principal a la derecha, con borde inferior. Sustituye al
 *    PageHeader grande cuando la pantalla vive incrustada en el hub.
 *  - SettingSection: rótulo en mayúsculas + divisor, agrupa filas.
 *  - SettingRow: etiqueta y descripción a la izquierda, el control a la
 *    derecha (ancho fijo en escritorio, completo en móvil).
 */
import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { FP } from "./tokens";

export function SectionPageHeader({ icon: Icon, title, description, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        mb: 3,
        pb: 2.5,
        borderBottom: `1px solid ${FP.color.border}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {Icon && <Icon sx={{ fontSize: 26, color: FP.color.textSecondary }} />}
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: FP.color.textPrimary }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
  );
}

SectionPageHeader.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  action: PropTypes.node,
};

SectionPageHeader.defaultProps = { icon: null, description: null, action: null };

export function SettingSection({ label, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          display: "block",
          py: 1.25,
          color: FP.color.navy,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          fontSize: "0.72rem",
          textAlign: "left",
        }}
      >
        {label}
      </Typography>
      <Divider />
      {children}
    </Box>
  );
}

SettingSection.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

SettingSection.defaultProps = { children: null };

export function SettingRow({ label, description, children, last }) {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          py: 2,
          gap: { xs: 1, sm: 4 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
            {label}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}>{children}</Box>
      </Box>
      {!last && <Divider />}
    </>
  );
}

SettingRow.propTypes = {
  label: PropTypes.node.isRequired,
  description: PropTypes.node,
  children: PropTypes.node,
  last: PropTypes.bool,
};

SettingRow.defaultProps = { description: null, children: null, last: false };
